import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormNumber,
  FormPrice,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { Loader2Icon, SaveIcon } from "lucide-react";
import { z } from "zod";
import type { RoleFeeConfigDTO } from "../../domain/contracts";
import { ROLE_FEE_KIND_LABELS } from "../../domain/contracts";
import { useRoleFeeConfigsQuery, useUpdateRoleFeeConfig } from "../api/queries";

const RoleFeeConfigFormSchema = z.object({
  showBaseAmount: z.coerce.number().int().min(0),
  additionalShowPercent: z.coerce.number().int().min(0).max(1000),
  rehearsalDayAmount: z.coerce.number().int().min(0),
  multiShowRehearsalPercent: z.coerce.number().int().min(0).max(1000),
  status: z.enum(["active", "inactive"]),
});

type RoleFeeConfigFormValues = z.infer<typeof RoleFeeConfigFormSchema>;

function RoleFeeConfigCard({
  config,
  canUpdate,
}: {
  config: RoleFeeConfigDTO;
  canUpdate: boolean;
}) {
  const update = useUpdateRoleFeeConfig();
  const methods = RHF.useForm<RoleFeeConfigFormValues>({
    resolver: zodResolver(RoleFeeConfigFormSchema),
    defaultValues: {
      showBaseAmount: config.showBaseAmount,
      additionalShowPercent: config.additionalShowPercent,
      rehearsalDayAmount: config.rehearsalDayAmount,
      multiShowRehearsalPercent: config.multiShowRehearsalPercent,
      status: config.status,
    },
  });

  const submitting = update.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3 text-base">
          <span>{ROLE_FEE_KIND_LABELS[config.roleFeeKind]}</span>
          <span className="rounded-full bg-muted px-2 py-1 font-normal text-muted-foreground text-xs">
            {config.roleFeeKind}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <FormRoot
          methods={methods}
          onSubmit={(values) =>
            update.mutate({
              roleFeeKind: config.roleFeeKind,
              input: values,
            })
          }
          className="grid gap-4"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <FormPrice
              name="showBaseAmount"
              label="ຄ່າໂຊພື້ນຖານ"
              currencySymbol="₭"
              min={0}
              disabled={!canUpdate || submitting}
            />
            <FormNumber
              name="additionalShowPercent"
              label="ໂຊເພີ່ມ (%)"
              min={0}
              max={1000}
              disabled={!canUpdate || submitting}
            />
            <FormPrice
              name="rehearsalDayAmount"
              label="ຄ່າຊ້ອມຕໍ່ວັນ"
              currencySymbol="₭"
              min={0}
              disabled={!canUpdate || submitting}
            />
            <FormNumber
              name="multiShowRehearsalPercent"
              label="ຄ່າຊ້ອມເມື່ອຫຼາຍໂຊ (%)"
              min={0}
              max={1000}
              disabled={!canUpdate || submitting}
            />
          </div>
          <FormSelect
            name="status"
            label="ສະຖານະ"
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
          />
          {canUpdate ? (
            <Button type="submit" disabled={submitting} className="w-fit">
              {submitting ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <SaveIcon className="size-4" />
              )}
              ບັນທຶກ
            </Button>
          ) : null}
        </FormRoot>
      </CardContent>
    </Card>
  );
}

export function RoleFeeConfigsPage() {
  const configs = useRoleFeeConfigsQuery();
  const canUpdate = useActionPermission(["performance-role-fees:update"]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄ່າຕົວຕາມບົດບາດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6">
          <h2 className="font-bold text-2xl tracking-tight">
            ຕັ້ງຄ່າຄ່າຕົວຕາມບົດບາດ
          </h2>
          <p className="text-muted-foreground">
            ກຳນົດອັດຕາຄ່າໂຊ ແລະ ຄ່າຊ້ອມສຳລັບນັກສະແດງ ນັກສະແດງທີ່ຜ່ານເກນ ອອກແບບທ່າເຕັ້ນສຳລັບລິລະປິນ ອອກແບບທ່າເຕັ້ນ ແລະ ອອກແບບການສະແດງ
          </p>
        </div>

        {configs.isLoading ? (
          <div className="rounded-xl border bg-card p-8 text-center text-muted-foreground">
            ກຳລັງໂຫຼດ...
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {(configs.data?.items ?? []).map((config) => (
              <RoleFeeConfigCard
                key={config.roleFeeKind}
                config={config}
                canUpdate={canUpdate}
              />
            ))}
          </div>
        )}
      </Main>
    </>
  );
}
